import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayAppend,
  UseFieldArrayMove,
} from "react-hook-form";
import { FormSwitch, FormTextField } from "../../../form-components";
import { InventoryItem, Invoice, InvoiceLineItem } from "../../../../models";
import { FormAutocomplete } from "../../../form-components";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { WarrantyModal } from "./Warranty.modal";

interface Props {
  invoice: Invoice;
  control: Control<Invoice, any>;
  fields: FieldArrayWithId<Invoice, "invoiceLineItems", "id">[];
  errors: FieldErrors<Invoice>;
  inventoryItems: InventoryItem[];
  loading: boolean;
  append: UseFieldArrayAppend<Invoice, "invoiceLineItems">;
  move: UseFieldArrayMove;
  onRemoveItem?: (index: number) => void;
  onInventoryItemInputChanged: (searchTerm: string) => void;
  onInventoryItemChanged: (inventoryItem: InventoryItem, index: number) => void;
  resolveDefaultItem: (
    field: ControllerRenderProps<Invoice, `invoiceLineItems.${number}`>
  ) => InventoryItem | undefined;
  onCalculate: () => void;
}

export const InvoiceLineItems = (props: Props) => {
  const {
    invoice,
    control,
    fields,
    errors,
    inventoryItems,
    loading,
    append,
    move,
    onRemoveItem,
    onInventoryItemInputChanged,
    onInventoryItemChanged,
    resolveDefaultItem,
    onCalculate,
  } = props;

  const handleDrag = ({ source, destination }: DropResult) => {
    if (destination) {
      move(source.index, destination.index);
    }
  };

  return (
    <FormControl error={errors.invoiceLineItems && !!errors.invoiceLineItems}>
      <DragDropContext onDragEnd={handleDrag}>
        <Stack direction="column" spacing={1}>
          <Droppable droppableId="test-items">
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {fields.map((item, index) => (
                  <Draggable
                    key={`invoiceLineItems[${index}]`}
                    draggableId={`item-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Controller
                        name={`invoiceLineItems.${index}`}
                        control={control}
                        key={item.id}
                        render={({ field }) => (
                          <Stack
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="center"
                            spacing={2}
                            key={item.id}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <b>{index + 1}</b>
                            <FormAutocomplete
                              name={`invoiceLineItems.${index}.inventoryItem`}
                              label="Inventory Item"
                              labelKeys={[
                                // "inventoryType.name",
                                "name",
                                "model",
                                "price",
                              ]}
                              options={inventoryItems ?? []}
                              onChange={(inventoryItem) =>
                                onInventoryItemChanged(inventoryItem, index)
                              }
                              loading={loading}
                              onInputChange={onInventoryItemInputChanged}
                              defaultValue={resolveDefaultItem(field)}
                              control={control}
                              separator="|"
                              sx={{ width: 500 }}
                            />
                            <FormTextField
                              name={`invoiceLineItems.${index}.cost`}
                              label="Cost"
                              onInputChanged={onCalculate}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              }}
                              type="number"
                              control={control}
                              sx={{ width: 100 }}
                            />
                            <FormTextField
                              name={`invoiceLineItems.${index}.price`}
                              label="Price"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              }}
                              onInputChanged={onCalculate}
                              type="number"
                              control={control}
                              sx={{ width: 100 }}
                            />
                            <FormTextField
                              name={`invoiceLineItems.${index}.quantity`}
                              label="Quantity"
                              control={control}
                              onInputChanged={onCalculate}
                              type="number"
                              sx={{ width: 100 }}
                            />
                            <FormTextField
                              name={`invoiceLineItems.${index}.display`}
                              label="Description"
                              control={control}
                              sx={{ width: 200 }}
                              multiline
                            />
                            {invoice.invoiceLineItems &&
                              invoice.invoiceLineItems[index]?.inventoryItem
                                ?.inventoryType
                                ?.showInInvoiceCategorySection && (
                                <FormTextField
                                  name={`invoiceLineItems.${index}.serialNumber`}
                                  label="Serial Number"
                                  control={control}
                                  sx={{ width: 200 }}
                                />
                              )}
                            <FormSwitch
                              name={`invoiceLineItems.${index}.hide`}
                              label="Hide"
                              control={control}
                            />
                            <IconButton
                              onClick={() =>
                                onRemoveItem && onRemoveItem(index)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                            <div {...provided.dragHandleProps}>
                              <DragIndicatorIcon />
                            </div>
                            {invoice.invoiceLineItems &&
                              invoice.invoiceLineItems[index]?.inventoryItem
                                ?.inventoryType
                                ?.showInInvoiceCategorySection && (
                                <WarrantyModal
                                  control={control}
                                  index={index}
                                  field={field}
                                />
                              )}
                          </Stack>
                        )}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </Stack>
      </DragDropContext>
      <Button
        onClick={() =>
          append(
            {
              quantity: 1,
              hide: false,
            } as InvoiceLineItem,
            {
              shouldFocus: true,
            }
          )
        }
        endIcon={<AddIcon />}
      >
        Add Line Item
      </Button>
      <FormHelperText>
        {errors.invoiceLineItems && errors.invoiceLineItems?.message}
      </FormHelperText>
    </FormControl>
  );
};
